export interface ApiEnvelope<T> {
  statusCode: number;
  status: boolean;
  message: string;
  data: T;
}

export function unwrapApiData<T>(response: ApiEnvelope<T>): T {
  return response.data;
}

export function formatInr(amount: number | string | null | undefined): string {
  if (amount == null || amount === '') {
    return '—';
  }
  const rawString =
    typeof amount === 'string' ? amount.trim().replace(/[₹,\s]/g, '') : null;
  const n =
    typeof amount === 'number' ? amount : Number.parseFloat(String(amount));
  if (!Number.isFinite(n)) {
    return String(amount);
  }
  const fractionDigits = resolveInrFractionDigits(rawString, n);
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: fractionDigits.min,
    maximumFractionDigits: fractionDigits.max,
  }).format(n);
}

function resolveInrFractionDigits(
  rawString: string | null,
  amount: number,
): { min: number; max: number } {
  if (rawString?.includes('.')) {
    const fraction = rawString.split('.')[1]?.replace(/[^\d]/g, '') ?? '';
    if (fraction.length > 0) {
      const digits = Math.min(fraction.length, 10);
      return { min: digits, max: digits };
    }
  }
  if (!Number.isInteger(amount)) {
    const fraction = String(amount).split('.')[1] ?? '';
    if (fraction.length > 0) {
      const digits = Math.min(fraction.length, 10);
      return { min: digits, max: digits };
    }
  }
  return { min: 0, max: 2 };
}

export function formatPortalStatus(status: string | null | undefined): string {
  if (!status) {
    return '—';
  }
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Loose API object — use {@link field} / {@link rec} to satisfy noPropertyAccessFromIndexSignature. */
export type ApiRecord = Record<string, unknown>;

export function str(value: unknown, fallback = ''): string {
  if (value == null) {
    return fallback;
  }
  return String(value);
}

export function rec(value: unknown): ApiRecord {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as ApiRecord)
    : {};
}

export function field(obj: ApiRecord | null | undefined, key: string, fallback = ''): string {
  return str(obj?.[key], fallback);
}

export function numField(obj: ApiRecord | null | undefined, key: string, fallback = 0): number {
  const value = obj?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
}
